/**
 * The reserved route register, and the rules for an administration slug.
 *
 * One list, in one file. A slug that collides with a real route would take a
 * part of the product away from everyone on the deployment, so the check
 * cannot live in the wizard component where a second surface would forget it.
 * The server validates against this same list — the client copy is for telling
 * someone why before they submit, not for deciding.
 */

/** Every path segment the platform already owns at the root. */
export const RESERVED_ROUTES: readonly string[] = [
  // Infrastructure and conventions
  'api',
  'install',
  'assets',
  'static',
  'public',
  'health',
  'healthz',
  'favicon.ico',
  'robots.txt',
  'sitemap.xml',
  'manifest.json',
  '_next',
  'well-known',
  // Identity
  'login',
  'logout',
  'signin',
  'signup',
  'register',
  'verify',
  'recover',
  'reset',
  'onboarding',
  'workspaces',
  'no-access',
  'settings',
  // Product surfaces that exist today
  'admin',
  'welcome',
  'dashboard',
  'housing',
  'roommates',
  'discover',
  'academics',
  'community',
  'community-workspace',
  'social',
  'market',
  'marketplace',
  'money',
  'notes',
  'notifications',
  'opportunities',
  'services',
  'support',
  'profile',
  'identity',
  'network',
  'merchant',
  'provider',
  'property',
  'employer',
  'institution',
  'legal',
  'search',
]

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/

export interface SlugVerdict {
  ok: boolean
  /** Why it was refused, written for the person choosing it. */
  reason: string | null
}

/**
 * The administration slug is an obscurity measure, not an authorisation one —
 * so the rules here are about not breaking the deployment, not about secrecy.
 */
export function checkAdminSlug(raw: string): SlugVerdict {
  const slug = raw.trim().toLowerCase()

  if (slug.length === 0) return { ok: false, reason: 'Choose a URL for administration.' }
  if (slug.length < 3) return { ok: false, reason: 'Use at least 3 characters.' }
  if (slug.length > 40) return { ok: false, reason: 'Use 40 characters or fewer.' }
  if (slug !== raw.trim()) {
    return { ok: false, reason: 'Use lower-case letters only, so the URL is unambiguous.' }
  }
  if (slug.includes('/') || slug.includes('.') || slug.includes('..')) {
    return { ok: false, reason: 'Use a single path segment — no slashes or dots.' }
  }
  if (!SLUG_PATTERN.test(slug)) {
    return {
      ok: false,
      reason: 'Use lower-case letters, numbers and hyphens, starting and ending with a letter or number.',
    }
  }
  if (RESERVED_ROUTES.includes(slug)) {
    return {
      ok: false,
      reason: `“${slug}” is already part of the platform. Choosing it would take that page away from everyone here.`,
    }
  }
  return { ok: true, reason: null }
}

/** The slug used before installation, and if configuration cannot be read. */
export const DEFAULT_ADMIN_SLUG = 'admin'
